import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface StateData {
  new: number;
  in_progress: number;
  on_hold: number;
  awaiting_info: number;
  resolved: number;
  closed: number;
  cancelled: number;
}

interface ServiceRequestStateBarChartProps {
  data: StateData;
  onBarClick?: (state: string) => void;
}

const stateLabels: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  awaiting_info: 'Awaiting Info',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const stateColors: Record<string, string> = {
  new: 'rgba(59, 130, 246, 0.8)',        // blue
  in_progress: 'rgba(245, 158, 11, 0.8)', // amber
  on_hold: 'rgba(249, 115, 22, 0.8)',    // orange
  awaiting_info: 'rgba(139, 92, 246, 0.8)', // purple
  resolved: 'rgba(16, 185, 129, 0.8)',   // green
  closed: 'rgba(34, 197, 94, 0.8)',      // emerald
  cancelled: 'rgba(107, 114, 128, 0.8)', // gray
};

const ServiceRequestStateBarChart: React.FC<ServiceRequestStateBarChartProps> = ({ data, onBarClick }) => {
  const stateKeys = Object.keys(stateLabels);
  const labels = stateKeys.map(key => stateLabels[key]);
  const values = stateKeys.map(key => data[key as keyof StateData] || 0);
  const colors = stateKeys.map(key => stateColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Service Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} requests (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 12,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      },
      y: {
        grid: { display: false },
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(stateKeys[index]);
      }
    },
  };

  return (
    <div id="chart-sr-state" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">State-wise Distribution</h3>
        <span className="text-xs text-gray-500">Click to filter</span>
      </div>
      <div className="h-72">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ServiceRequestStateBarChart;
